import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import KpiStatCard from "../components/KpiStatCard";
import CreateStockOutModal from "../components/CreateStockOutModal";
import SiteSearchSelector from "../components/SiteSearchSelector";
import { safeText } from "../utils/hubHelpers";

export default function StockOverview({ onOpenCreateGrn }) {
  const [loading, setLoading] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStockType, setSelectedStockType] = useState("");
  const [isStockOutModalOpen, setIsStockOutModalOpen] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    fetchStockItems();
  }, [selectedSiteId]);

  const fetchSites = async () => {
    try {
      const res = await callApi(apiUrl.get_site, { method: "GET" });
      if (res?.status === true) {
        const rawList = Array.isArray(res?.data?.site)
          ? res.data.site
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const list = rawList
          .map((s) => (s?.site ? { ...s.site, ...s } : s))
          .filter(Boolean);
        setSites(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchStockItems = async () => {
    setLoading(true);
    try {
      let query = "?";
      if (selectedSiteId) query += `siteId=${selectedSiteId}&`;
      if (searchQuery) query += `search=${encodeURIComponent(searchQuery)}&`;

      const res = await callApi(`${apiUrl.get_purchase_item}${query}`, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.rows)
          ? res.data.rows
          : [];
        setStockItems(list);
      } else {
        setStockItems([]);
      }
    } catch (e) {
      console.error(e);
      setStockItems([]);
    } finally {
      setLoading(false);
    }
  };

  const safeStock = Array.isArray(stockItems) ? stockItems : [];

  const filteredStock = safeStock.filter((it) => {
    if (!it) return false;
    if (selectedStockType === "1") {
      if (it.stockType !== 1 && it.stockType !== "1") return false;
    } else if (selectedStockType === "0") {
      if (it.stockType === 1 || it.stockType === "1") return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (it.name || "").toLowerCase();
      const hsn = (it.hsnCode || "").toLowerCase();
      const unit = (it.unit || "").toLowerCase();
      if (!name.includes(q) && !hsn.includes(q) && !unit.includes(q)) return false;
    }
    return true;
  });

  const totalDistinctItems = safeStock.length;
  const consumableCount = safeStock.filter((i) => i.stockType === 1 || i.stockType === "1").length;
  const totalStockUnits = safeStock.reduce((sum, item) => sum + (parseFloat(item.totalCurrentStock) || 0), 0);
  const lowStockCount = safeStock.filter((i) => (parseFloat(i.totalCurrentStock) || 0) <= 5).length;

  return (
    <div>
      {/* 1. KPI Stats Grid */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Tracked Stock Items"
          value={totalDistinctItems}
          subtext="Parts, consumables & spares"
          icon={<span>📦</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Total Available Units"
          value={totalStockUnits.toLocaleString()}
          subtext="Aggregated across active site"
          icon={<span>📊</span>}
          color="#10b981"
          iconBg="#ecfdf5"
        />

        <KpiStatCard
          label="Consumable Spares"
          value={consumableCount}
          subtext="Filters, oils, grease & hardware"
          icon={<span>🛢️</span>}
          color="#8b5cf6"
          iconBg="#f5f3ff"
        />

        <KpiStatCard
          label="Low Stock Alerts"
          value={lowStockCount}
          subtext="≤ 5 units remaining"
          icon={<span>⚠️</span>}
          color="#ef4444"
          iconBg="#fef2f2"
        />
      </div>

      {/* 2. Searchable Site Selector */}
      <SiteSearchSelector
        sites={sites}
        selectedSiteId={selectedSiteId}
        onSelectSite={setSelectedSiteId}
        countsMap={{}}
        totalCount={sites.length}
      />

      {/* 3. Toolbar */}
      <div className="hub-toolbar">
        <div className="toolbar-left">
          <div className="search-box-wrap">
            <span className="search-icon-pos">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search part name, HSN code, unit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchStockItems()}
            />
          </div>

          <select
            className="filter-select"
            value={selectedStockType}
            onChange={(e) => setSelectedStockType(e.target.value)}
          >
            <option value="">All Stock Types</option>
            <option value="1">Consumable Spares</option>
            <option value="0">Fixed Assets / Components</option>
          </select>
        </div>

        <div className="toolbar-right" style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onOpenCreateGrn}
            style={{
              background: "#059669",
              borderColor: "#059669",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              color: "#fff",
            }}
          >
            <span>📥</span> + Inward (GRN)
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsStockOutModalOpen(true)}
            style={{
              background: "#d97706",
              borderColor: "#d97706",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              color: "#fff",
            }}
          >
            <span>⚡</span> Quick Issue / Stock Out
          </button>
        </div>
      </div>

      {/* 4. Stock Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status" style={{ marginBottom: "1rem" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Loading Inventory Levels...</p>
        </div>
      ) : filteredStock.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: "4rem 2rem",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem" }}>📦</span>
          <h4 style={{ color: "#0f172a", fontWeight: 700 }}>No Inventory Records Found</h4>
          <p style={{ color: "#64748b", maxWidth: 420, margin: "0 auto 1.5rem auto", fontSize: "0.875rem" }}>
            No stock items found for the selected site. Goods Received Notes (GRN) populate site inventory automatically.
          </p>
        </div>
      ) : (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", color: "#475569", textTransform: "uppercase", fontSize: "11.5px", letterSpacing: "0.04em" }}>
                <th style={{ padding: "0.85rem 1.15rem", textAlign: "left", fontWeight: 700 }}>Item & Part Description</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>HSN Code</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Category / Type</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Total In (GRN)</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Issued (Stock Out)</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Current Balance</th>
                <th style={{ padding: "0.85rem 1.15rem", textAlign: "left", fontWeight: 700 }}>Stock Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStock.map((it) => {
                const curStock = parseFloat(it.totalCurrentStock) || 0;
                const recQty = parseFloat(it.totalReceivedQuantity) || 0;
                const outQty = parseFloat(it.totalStockOutQuantity) || 0;
                const isLow = curStock <= 5;

                return (
                  <tr key={it.id || Math.random()} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "0.85rem 1.15rem", textAlign: "left" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "13.5px" }}>{safeText(it.name, "Item")}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>Unit: {safeText(it.unit, "Units")}</div>
                    </td>
                    <td style={{ padding: "0.85rem 1rem", color: "#2563eb", fontFamily: "monospace", fontWeight: 700, textAlign: "left", fontSize: "13px" }}>
                      {safeText(it.hsnCode, "—")}
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                      <span style={{ fontSize: "12px", background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, fontWeight: 600, color: "#334155" }}>
                        {it.stockType === 1 || it.stockType === "1" ? "Consumable Spare" : "Fixed Asset"}
                      </span>
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700, color: "#059669", fontSize: "13px" }}>
                      +{recQty} {safeText(it.unit, "")}
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700, color: "#dc2626", fontSize: "13px" }}>
                      -{outQty} {safeText(it.unit, "")}
                    </td>
                    <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                      <span style={{ fontSize: "14px", fontWeight: 800, color: isLow ? "#dc2626" : "#0f172a" }}>
                        {curStock} {safeText(it.unit, "")}
                      </span>
                    </td>
                    <td style={{ padding: "0.85rem 1.15rem", textAlign: "left" }}>
                      {curStock <= 0 ? (
                        <span style={{ color: "#dc2626", background: "#fee2e2", padding: "3px 9px", borderRadius: 12, fontSize: "12px", fontWeight: 700 }}>
                          Out of Stock
                        </span>
                      ) : isLow ? (
                        <span style={{ color: "#b45309", background: "#fef3c7", padding: "3px 9px", borderRadius: 12, fontSize: "12px", fontWeight: 700 }}>
                          Low Stock
                        </span>
                      ) : (
                        <span style={{ color: "#059669", background: "#dcfce7", padding: "3px 9px", borderRadius: 12, fontSize: "12px", fontWeight: 700 }}>
                          Available
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock Out Modal */}
      <CreateStockOutModal
        isOpen={isStockOutModalOpen}
        toggle={() => setIsStockOutModalOpen(!isStockOutModalOpen)}
        defaultSiteId={selectedSiteId}
        onSuccess={fetchStockItems}
      />
    </div>
  );
}
